import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users, FileText, TrendingUp, Activity, Clock, Award,
  ArrowUp, ArrowDown, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-12 h-12 text-ssdp-teal animate-spin" />
      </div>
    );
  }

  const metrics = [
    {
      title: 'Total Users',
      value: analytics?.users?.total || 0,
      change: `+${analytics?.users?.new_7d || 0} this week`,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      title: 'Registered Users',
      value: analytics?.users?.registered || 0,
      subtitle: `${analytics?.users?.anonymous || 0} anonymous`,
      icon: Activity,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      title: 'Active Users (7d)',
      value: analytics?.users?.active_7d || 0,
      subtitle: 'Weekly active',
      icon: TrendingUp,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    },
    {
      title: 'Total Scenarios',
      value: analytics?.scenarios?.published || 0,
      subtitle: `${analytics?.scenarios?.total || 0} total`,
      icon: FileText,
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600'
    },
    {
      title: 'Completions',
      value: analytics?.engagement?.total_completions || 0,
      subtitle: 'Scenarios completed',
      icon: Award,
      color: 'from-pink-500 to-pink-600',
      bgColor: 'bg-pink-50',
      textColor: 'text-pink-600'
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back! 👋
        </h1>
        <p className="text-gray-600">
          Here's what's happening with Pathways today
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {metrics.map((metric, index) => (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${metric.bgColor}`}>
                <metric.icon className={`w-6 h-6 ${metric.textColor}`} />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {metric.value.toLocaleString()}
            </h3>
            <p className="text-sm font-medium text-gray-600 mb-1">
              {metric.title}
            </p>
            {metric.subtitle && (
              <p className="text-xs text-gray-500">{metric.subtitle}</p>
            )}
            {metric.change && (
              <p className="text-xs text-green-600 font-medium mt-2 flex items-center gap-1">
                <ArrowUp className="w-3 h-3" />
                {metric.change}
              </p>
            )}
          </motion.div>
        ))}
      </div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
          <Clock className="w-5 h-5 text-gray-400" />
        </div>

        {analytics?.recent_activity && analytics.recent_activity.length > 0 ? (
          <div className="space-y-4">
            {analytics.recent_activity.slice(0, 5).map((activity, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
              >
                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-ssdp-teal" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    Scenario: {activity.scenario_id}
                  </p>
                  <p className="text-xs text-gray-500">
                    User: {activity.user_id?.slice(0, 12)}...
                    {activity.completed && ' · Completed'}
                  </p>
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(activity.timestamp).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>No recent activity</p>
          </div>
        )}
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <button
          onClick={() => window.location.href = '/admin/scenarios/new'}
          className="p-6 bg-gradient-to-br from-ssdp-teal to-ssdp-purple text-white rounded-2xl hover:shadow-xl transition-all"
        >
          <FileText className="w-8 h-8 mb-3" />
          <h3 className="font-bold text-lg mb-1">Create Scenario</h3>
          <p className="text-sm opacity-90">Add new learning content</p>
        </button>

        <button
          onClick={() => window.location.href = '/admin/analytics'}
          className="p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-indigo-300 hover:shadow-lg transition-all"
        >
          <TrendingUp className="w-8 h-8 mb-3 text-ssdp-teal" />
          <h3 className="font-bold text-lg mb-1 text-gray-900">View Analytics</h3>
          <p className="text-sm text-gray-600">Detailed insights</p>
        </button>

        <button
          onClick={() => window.location.href = '/admin/users'}
          className="p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-indigo-300 hover:shadow-lg transition-all"
        >
          <Users className="w-8 h-8 mb-3 text-ssdp-teal" />
          <h3 className="font-bold text-lg mb-1 text-gray-900">Manage Users</h3>
          <p className="text-sm text-gray-600">View all users</p>
        </button>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
